/**
 * @file - Dialog handler service.
 */
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { InfoDialogComponent } from '../../components/info-dialog/info-dialog.component';
import { FormDialogComponent } from '../../components/form-dialog/form-dialog.component';
import { ConfirmationDialog } from '../../models/dialog.model';
import { OtpConfirmationDialogComponent } from '../../components/otp-confirmation-dialog/otp-confirmation-dialog.component';
import { RideshareDialogComponent } from '../../components/rideshare-dialog/rideshare-dialog.component';
import { TermsDialogComponent } from '../../components/terms-dialog/terms-dialog.component';
import { TosDialogComponent } from '../../components/tos-dialog/tos-dialog.component';
import { FedexLocationLookupDialogComponent } from '../../components/fedex-location-lookup-dialog/fedex-location-lookup-dialog.component';
import { UberDialogComponent } from '../../components/uber/uber-dialog/uber-dialog.component';
import { ConfirmationDialogChangedMessageComponent } from '../../components/confirmation-dialog-changed-message/confirmation-dialog-changed-message.component';
import { UpdateContactInfoDialogComponent} from '../../components/update-contact-info-dialog/update-contact-info-dialog.component';
import { UberDisconnectDialogComponent } from '../../components/uber-disconnect-dialog/uber-disconnect-dialog.component';
import { ChooseCashoutMethodDialogComponent, ChooseCashoutMethodDialogData } from '../../components/choose-cashout-method-dialog/choose-cashout-method-dialog.component';

/**
 *
 */
@Injectable({
    providedIn: 'root',
})
export class DialogHandlerService {
    /**
     * Constructor method.
     *
     * @param {MatDialog} dialogRef - Dialog reference.
     */
    constructor(private dialogRef: MatDialog) {}

    /**
     * Show alert dialog.
     *
     * @param {ConfirmationDialog} data - Dialog data.
     */
    alert(data: ConfirmationDialog): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = 'info-dialog';
        this.dialogRef.closeAll();
        this.dialogRef.open(ConfirmationDialogComponent, dialogConfig);
    }

    /**
     * Shows alert dialog with callback.
     *
     * @param {ConfirmationDialog} data - Dialog data.
     * @returns {Observable<any>} - Observable.
     */
    alertWithCallback(data: ConfirmationDialog): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = 'info-dialog';
        const confirmActionDialog = this.dialogRef.open(
            ConfirmationDialogComponent,
            dialogConfig
        );
        return confirmActionDialog.afterClosed();
    }

    /**
     * Open info dialog.
     *
     * @param {any} data - Dialog data.
     */
    openInfoDialog(data: any): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.maxWidth = 652;
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        this.dialogRef.open(InfoDialogComponent, dialogConfig);
    }

    /**
     * Shows confirmation dialog.
     *
     * @param {ConfirmationDialog} data - Dialog data.
     * @param {boolean} confirmationModal - Confirmation modal.
     * @returns {Observable<any>} - Observable.
     */
    confirmAction(
        data: ConfirmationDialog,
        confirmationModal: boolean = true
    ): Observable<any> {
        data.confirmationModal = confirmationModal;
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        const confirmActionDialog = this.dialogRef.open(
            ConfirmationDialogComponent,
            dialogConfig
        );
        return confirmActionDialog.afterClosed();
    }

    /**
     * Shows FedEx address suggestion dialog.
     *
     * @param {any} data - Dialog data.
     * @returns {Observable<any>} - Observable.
     */
    showFedexAddressSuggestion(data: any): Observable<any> {
        data.confirmationModal = true;
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        const confirmActionDialog = this.dialogRef.open(
            FormDialogComponent,
            dialogConfig
        );
        return confirmActionDialog.afterClosed();
    }

    /**
     * Shows continue without FedEx dialog.
     *
     * @param {any} data - Dialog data.
     * @returns {Observable<any>} - Observable.
     */
    showContinueWithoutFedEx(data: any): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        dialogConfig.disableClose = false;
        const dialog = this.dialogRef.open(FormDialogComponent, dialogConfig);
        return dialog.afterClosed();
    }

    /**
     * Shows OTP confirmation modal.
     *
     * @param {any} data - Dialog data.
     * @returns {Observable<any>} - Observable.
     */
    showOtpConfirmationModal(data: any): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        dialogConfig.disableClose = true;
        const dialog = this.dialogRef.open(
            OtpConfirmationDialogComponent,
            dialogConfig
        );
        return dialog.afterClosed();
    }

    /**
     * Shows Uber modal.
     *
     * @param {any} data - Dialog data.
     * @returns {Observable<any>} - Observable.
     */
    showUberModal(data: any): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog-large';
        dialogConfig.disableClose = true;
        const dialog = this.dialogRef.open(
            RideshareDialogComponent,
            dialogConfig
        );
        return dialog.afterClosed();
    }

    /**
     * Shows terms modal.
     *
     * @param {any} type - Dialog type.
     * @returns {Observable<any>} - Observable.
     */
    showTermsModal(type: any): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = type;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog-large';
        dialogConfig.disableClose = true;
        const dialog = this.dialogRef.open(TermsDialogComponent, dialogConfig);
        return dialog.afterClosed();
    }

    /**
     * Shows TOS modal.
     *
     * @param {any} type - Dialog type.
     * @returns {Observable<any>} - Observable.
     */
    showTosModal(type: any): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = type;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        dialogConfig.disableClose = true;
        const dialog = this.dialogRef.open(TosDialogComponent, dialogConfig);
        return dialog.afterClosed();
    }

    /**
     * Shows Uber modal.
     *
     * @param {any} type - Dialog type.
     * @returns {Observable<any>} - Observable.
     */
    showUberConnectModal(type: any): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = type;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'uber-dialog';
        dialogConfig.disableClose = true;
        const dialog = this.dialogRef.open(UberDialogComponent, dialogConfig);
        return dialog.afterClosed();
    }

    /**
     * Shows error dialog.
     *
     * @param {any} data - Dialog data.
     */
    errorDialog(data: any) {
        console.log(data.message);
    }

    /**
     * Shows FedEx Location Lookup dialog.
     *
     * @param {any} data - Dialog data.
     */
    showFedexLocationLookupDialog(data: any) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        dialogConfig.disableClose = true;

        const dialog = this.dialogRef.open(
            FedexLocationLookupDialogComponent,
            dialogConfig
        );
        return dialog.afterClosed();
    }

    /**
     * Shows confirmation dialog for uber redirect.
     *
     * @param {ConfirmationDialog} data - Dialog data.
     * @param {boolean} confirmationModal - Confirmation modal.
     * @returns {Observable<any>} - Observable.
     */
    confirmActionWithUberRedirect(data: {
        title: string;
        message: string;
        cancelBtnText: string;
        confirmBtnText: string;
    }): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        const confirmActionDialog = this.dialogRef.open(
            ConfirmationDialogChangedMessageComponent,
            dialogConfig
        );
        return confirmActionDialog.afterClosed();
    }

    /**
     * Shows Uber disconnect dialog with survey.
     *
     * @param {any} data - Dialog data.
     * @returns {Observable<any>} - Observable.
     */
    showUberDisconnectDialog(data: any): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        const dialog = this.dialogRef.open(
            UberDisconnectDialogComponent,
            dialogConfig
        );
        return dialog.afterClosed();
    }

    /** Shows contact info verification popup dialog.
     *
     * @param {any} data - User summary data to display in dialog.
     * @returns {Observable<any>} - Observable that emits when dialog is closed.
     */
    showContactInfoPopUp(data: any): Observable<any> {
        const dialogConfig= new MatDialogConfig();
        dialogConfig.data = data;
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        dialogConfig.disableClose = true;

        const dialog = this.dialogRef.open(
            UpdateContactInfoDialogComponent,
            dialogConfig
        );
        return dialog.afterClosed();
    }

    /**
     * Shows choose cashout method dialog.
     *
     * @param {any} data - Dialog data.
     * @returns {Observable<any>} - Observable that emits when dialog is closed.
     */
    showChooseCashoutMethodDialog(data: ChooseCashoutMethodDialogData = {}): Observable<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = 'dialog';
        dialogConfig.panelClass = 'info-dialog';
        dialogConfig.disableClose = false;
        dialogConfig.data = data;
        const dialog = this.dialogRef.open(
            ChooseCashoutMethodDialogComponent,
            dialogConfig
        );
        return dialog.afterClosed();
    }

    warningMessage = '';
}
